<template>
    <div v-if="isOpen" class="modal-overlay" @click="closeModal">
        <div class="modal-content" @click.stop>
            <div class="modal-header" v-if="currentStep != 4">
                <h3>Estimez le prix de rachat du véhicule</h3>
                <p><b>Gratuit & sans engagement.</b></p>
                <button class="close-button" @click="closeModal">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </button>
            </div>

            <div class="modal-body">
                <!-- Étape 1: Immatriculation et kilométrage -->
                <div class="steps-estimation" v-if="currentStep == 1">
                    <div class="block-progression">
                        <div class="arrow-number">
                            <span id="steps-number">1/3</span>
                        </div>
                        <div class="steps-bg"><div class="steps-orange"></div></div>
                    </div>

                    <div class="form">
                        <input type="text" v-model="form.mileage" name="kilometers" id="km" placeholder="Kilométrage" @input="formatNumber($event, 'mileage')" inputmode="numeric" pattern="[0-9\s]*" required>
                        <div class="label-immat">Immatriculation</div>
                        <input type="text" v-model="form.registration" v-mask="getRegistrationMask" masked="true" name="registration" id="immat" class="immat" placeholder="AA - 123 - AA" pattern="^[A-Z]{2} ?- ?\d{3} ?- ?[A-Z]{2}$" required>
                        <p id="errorInput" class="errorImmat">Votre immatriculation n'est pas reconnue</p>
                    </div>

                    <div class="bouton-orange" :class="{ 'disabled': !canGoToStep2 }" @click="canGoToStep2 ? goToStep2() : null">
                        <span id="buttonContent">Suivant</span>
                        <svg id="svgImage" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path id="buttonSvgPath" d="M8 0L6.59 1.41L12.17 7H0V9H12.17L6.59 14.59L8 16L16 8L8 0Z"/>
                        </svg>
                    </div>
                </div>

                <!-- Étape 2: Détails du véhicule -->
                <div class="steps-estimation" v-if="currentStep == 2">
                    <div class="block-progression">
                        <div class="arrow-number">
                            <button type="button" class="previous-arrow" @click="currentStep = 1" aria-label="Étape précédente">
                                <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M14 6 L8 12 L14 18" stroke="#F07D00" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
                                </svg>
                            </button>
                            <span id="steps-number">2/3</span>
                        </div>
                        <div class="steps-bg"><div class="steps-orange" style="width: 66.6667%;"></div></div>
                    </div>

                    <div class="form" id="autobizzResult">
                        <!-- Informations du véhicule -->
                        <div class="vehicle-info">
                            <div class="vehicle-model">{{autobizData.makeName}} {{autobizData.modelName}}</div>
                            <div class="vehicle-details">
                                <div class="detail-item">
                                    <img src="/img/carburant.svg" alt="carburant" class="detail-icon">
                                    <span>{{autobizData.fuelName}}</span>
                                </div>
                                <div class="detail-item">
                                    <img src="/img/couleur.svg" alt="couleur" class="detail-icon">
                                    <span>{{autobizData.color}}</span>
                                </div>
                            </div>
                            <p id="errorAutobizz" class="errorImmat">Votre immatriculation n'est pas reconnue</p>
                        </div>

                        <!-- Champs du formulaire -->
                        <div class="form-fields">
                            <div class="field-group">
                                <select name="finition" id="finition-select" v-model="form.finition" class="form-select" :class="{ 'placeholder-selected': !form.finition }">
                                    <option value="" disabled selected>Finition</option>
                                    <option v-for="version in autobizData.versions" :key="version.id" :value="version.name">{{version.name}}</option>
                                </select>
                            </div>

                            <div class="field-group">
                                <select name="etat" v-model="form.state" class="form-select" :class="{ 'placeholder-selected': !form.state }">
                                    <option value="" disabled selected>Etat</option>
                                    <option value="proche-neuf">Proche du neuf</option>
                                    <option value="bon">Bon</option>
                                    <option value="correct">Correct</option>
                                    <option value="mauvais">Mauvais</option>
                                    <option value="nul">Très mauvais, accidenté</option>
                                </select>
                            </div>

                            <div class="field-group">
                                <input type="text" v-model="form.estimation" placeholder="Votre estimation" class="form-input" @input="formatNumber($event, 'estimation'); validateEstimation()" inputmode="numeric" pattern="[0-9\s]*" required>
                                <div v-if="errors.estimation" class="error-message">{{ errors.estimation }}</div>
                            </div>

                            <div class="field-group">
                                <textarea v-model="form.note" placeholder="Commentaires" rows="4" class="form-textarea"></textarea>
                            </div>
                        </div>
                    </div>

                    <div class="bouton-orange" :class="{ 'disabled': !canGoToStep3 }" @click="canGoToStep3 ? currentStep = 3 : null">
                        <span id="buttonContent">Suivant</span>
                        <svg id="svgImage" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path id="buttonSvgPath" d="M8 0L6.59 1.41L12.17 7H0V9H12.17L6.59 14.59L8 16L16 8L8 0Z"/>
                        </svg>
                    </div>
                </div>

                <!-- Étape 3: Informations personnelles -->
                <div class="steps-estimation" v-if="currentStep == 3">
                    <div class="block-progression">
                        <div class="arrow-number">
                            <button type="button" class="previous-arrow" @click="currentStep = 2" aria-label="Étape précédente">
                                <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M14 6 L8 12 L14 18" stroke="#F07D00" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" fill="none"/>
                                </svg>
                            </button>
                            <span id="steps-number">3/3</span>
                        </div>
                        <div class="steps-bg"><div class="steps-orange" style="width:100%"></div></div>
                    </div>

                    <div class="form">
                        <input v-model="form.last_name" type="text" id="lastname" placeholder="Nom" required>
                        <input v-model="form.first_name" type="text" id="firstname" placeholder="Prénom" required>
                        <input v-model="form.email" type="email" id="email" placeholder="Email" @input="validateEmail" required>
                        <div v-if="errors.email" class="error-message">{{ errors.email }}</div>

                        <div class="location-fields">
                            <input v-model="form.postal_code" type="text" id="postalcode" placeholder="Code postal" @input="validatePostalCode" required maxlength="5" pattern="[0-9]{5}">
                            <input v-model="form.city" type="text" id="town" placeholder="Ville" required>
                        </div>
                        <div v-if="errors.postal_code" class="error-message">{{ errors.postal_code }}</div>

                        <input v-model="form.phone" type="tel" id="phone" placeholder="Téléphone" maxlength="14" @input="validatePhone">
                        <div v-if="errors.phone" class="error-message">{{ errors.phone }}</div>

                        <div class="agency-choice" v-if="shouldShowAgencyChoice">
                            <div class="agency-choice-header">
                                <input type="checkbox" id="agency-preference" v-model="form.prefer_agency" @change="onAgencyPreferenceChange">
                                <label for="agency-preference">
                                    Je souhaite être accompagné(e) par <span class="agency-name">{{ siteAgencyDisplayName }}</span>
                                    <button type="button" class="info-button" @click.prevent="toggleInfoTooltip" aria-label="Information sur le dispatch">
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                                            <path d="M12 16V12M12 8H12.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                                        </svg>
                                    </button>
                                </label>
                            </div>
                            <div class="info-tooltip" v-if="showInfoTooltip">
                                <div class="tooltip-content">
                                    <ul>
                                        <li><strong>Case cochée :</strong> votre demande est <strong>envoyée</strong> directement à {{ agency.name || 'Ewigo Reims' }}, pour un suivi personnalisé.</li>
                                        <li><strong>Case non cochée :</strong> votre demande est transmise au point de vente Ewigo le plus proche, selon le code postal renseigné.</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <hr v-if="shouldShowAgencyChoice">

                        <div class="accept">
                            <input
                                type="checkbox"
                                id="accept"
                                name="accept"
                                v-model="form.accept_contact"
                                :disabled="!consentReady || consentLoading"
                            >
                            <label for="accept" id="accept-label">
                                <span v-if="consentLoading" class="consent-loading">Recherche de l'agence en cours...</span>
                                <span v-else-if="consentReady" class="consent-text">
                                    J'accepte d'être contacté par {{ consentAgencyLabel }} par téléphone au sujet de la vente ou de la reprise de mon véhicule, jusqu'à 12 mois. Je peux retirer ce consentement à tout moment, y compris oralement, en appelant le {{ consentContact.phone }} ou en écrivant à <a class="consent-email-link" :href="`mailto:${consentContact.email}`" @click.stop>{{ consentContact.email }}</a>, ou directement au sein du point de vente. Je peux obtenir gratuitement, sur simple demande, la preuve de ce consentement.
                                </span>
                                <span v-else>Veuillez renseigner un code postal valide pour afficher le consentement.</span>
                            </label>
                        </div>

                        <div class="bouton-orange" :class="{ 'disabled': !canSubmitForm || loadReprise, 'loading': loadReprise }" @click="canSubmitForm && !loadReprise ? submitForm() : null" id="CTA-estimate">
                            <span v-if="!loadReprise">Estimer</span>
                            <span v-else>Traitement en cours...</span>
                            <svg v-if="!loadReprise" width="20" height="22" viewBox="0 0 20 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M2 0C0.895 0 0 0.895 0 2V18C0 19.105 0.895 20 2 20H12.4277C12.1437 19.337 12 18.641 12 18H2V6H14V8H16V2C16 0.895 15.105 0 14 0H2ZM2 2H14V4H2V2ZM4 8V10H6V8H4ZM7 8V10H9V8H7ZM10 8V10H12V8H10ZM16 10V11.1992C15 11.4992 14 12.4 14 14C14 15.9 15.5996 16.5004 16.5996 16.9004C17.7996 17.4004 18 17.5 18 18C18 18.5 17.8 19 17 19C16.2 19 16 18.5 16 18H14C14 19 14.6 20.4008 16 20.8008V22H18V20.8008C19.4 20.4008 20 19 20 18C20 16.1 18.4004 15.4996 17.4004 15.0996C16.2004 14.5996 16 14.5 16 14C16 13.2 16.5 13 17 13C17.8 13 18 13.5 18 14H19H20C20 13 19.4 11.5992 18 11.1992V10H16ZM4 11V13H6V11H4ZM7 11V13H9V11H7ZM10 11V13H12V11H10ZM4 14V16H6V14H4ZM7 14V16H12V14H7Z"/>
                            </svg>
                            <svg v-else class="spinner" width="20" height="20" viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
                                <circle class="spinner-path" cx="25" cy="25" r="20" fill="none" stroke="currentColor" stroke-width="5"></circle>
                            </svg>
                        </div>
                    </div>
                </div>

                <!-- Étape 4: Merci -->
                <div id="merci" class="merci" v-if="currentStep == 4">
                    <button class="close-button" @click="closeModal">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </button>
                    <span class="title-merci">Merci !</span>
                    <p>
                        Votre demande d'estimation a bien été prise en compte. Un conseiller revient vers vous rapidement.
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import _, {throttle} from 'lodash';
import {mask} from 'vue-the-mask'
import { trackGenerateLead } from '@/ewigo-analytics';

export default {
    directives: {mask},
    props: {
        isOpen: {
            type: Boolean,
            default: false
        },
        agency: {
            type: Object,
            required: true
        },
        agencyFranceId: {
            type: String,
            default: null
        },
        initialRegistration: {
            type: String,
            default: ''
        },
        initialMileage: {
            type: String,
            default: ''
        },
        reference: {
            type: String,
            default: ''
        },
        utmId: {
            type: String,
            default: ''
        },
        captcha: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            loadReprise: false,
            currentStep: 1,
            autobizData: false,
            showInfoTooltip: false,
            resolvedAgencyContact: null,
            consentLoading: false,
            consentResolveRequestId: 0,
            errors: {
                estimation: '',
                email: '',
                phone: '',
                postal_code: ''
            },
            form: this.$inertia.form({
                registration: this.initialRegistration,
                mileage: this.initialMileage,
                note: null,
                state: '',
                estimation: null,
                first_name: null,
                last_name: null,
                email: null,
                city: null,
                postal_code: null,
                phone: null,
                finition: '',
                reference: this.reference,
                make: null,
                model: null,
                fuel: null,
                color: null,
                year: null,
                utm: this.utmId,
                prefer_agency: true,
                accept_contact: false
            })
        }
    },
    watch: {
        autobizData: {
            deep: true,
            handler: _.debounce(function () {
                this.form.make = this.autobizData.makeName
                this.form.model = this.autobizData.modelName
                this.form.fuel = this.autobizData.fuelName
                this.form.color = this.autobizData.color
                this.form.year = this.autobizData.dateRelease
            }, 100)
        },
        isOpen(newVal) {
            if (newVal) {
                this.resetForm()
            }
        },
        currentStep(newVal) {
            if (newVal === 3) {
                this.fetchResolvedAgency()
            }
        },
        'form.postal_code': _.debounce(function () {
            if (this.currentStep === 3) {
                this.fetchResolvedAgency()
            }
        }, 400),
        'form.prefer_agency'() {
            if (this.currentStep === 3) {
                this.form.accept_contact = false
            }
        },
    },
    computed: {
        getRegistrationMask() {
            if (!this.form.registration) return 'X';
            if (!isNaN(this.form.registration.charAt(0))) {
                return '#### AAA ##';
            } else {
                return 'AA-###-AA';
            }
        },
        canGoToStep2() {
            return this.form.registration && this.form.registration.length >= 9;
        },
        canGoToStep3() {
            return this.form.finition && this.form.state && this.form.estimation && !this.errors.estimation;
        },
        canSubmitForm() {
            return this.form.first_name &&
                   this.form.last_name &&
                   this.form.email &&
                   this.form.city &&
                   this.form.postal_code &&
                   !this.errors.email &&
                   !this.errors.phone &&
                   this.form.accept_contact &&
                   this.consentReady &&
                   !this.consentLoading;
        },
        isFranceSite() {
            return this.agencyFranceId && String(this.agency.id) === String(this.agencyFranceId);
        },
        usesSiteAgencyForConsent() {
            if (this.isFranceSite) {
                return false;
            }

            if (this.utmId) {
                return true;
            }

            return this.form.prefer_agency && this.shouldShowAgencyChoice;
        },
        consentContact() {
            const postalCode = this.form.postal_code;

            if (!postalCode || !/^\d{5}$/.test(postalCode) || this.consentLoading) {
                return null;
            }

            if (this.usesSiteAgencyForConsent) {
                return {
                    type: 'agency',
                    name: this.formatAgencyDisplayName(this.agency.name),
                    phone: this.agency.phone,
                    email: this.agency.email,
                    agency_id: this.agency.id,
                };
            }

            return this.resolvedAgencyContact;
        },
        consentAgencyId() {
            if (this.consentContact?.type !== 'agency' || !this.consentContact?.agency_id) {
                return null;
            }

            return this.consentContact.agency_id;
        },
        consentReady() {
            return Boolean(this.consentContact && this.consentText);
        },
        consentText() {
            return this.buildConsentText(this.consentContact);
        },
        consentAgencyLabel() {
            if (!this.consentContact) {
                return '';
            }

            if (this.consentContact.type === 'france') {
                return `${this.consentContact.name} (une des agences Ewigo me rappellera)`;
            }

            return this.consentContact.name;
        },
        shouldShowAgencyChoice() {
            // S'il y a un cookie UTM, le lead est toujours attribué à l'agence actuelle : on ne montre pas le choix
            if (this.utmId) return false;
            // Afficher uniquement si l'agence actuelle n'est pas l'agence France
            return this.agencyFranceId && String(this.agency.id) !== String(this.agencyFranceId);
        },
        siteAgencyDisplayName() {
            return this.formatAgencyDisplayName(this.agency.name || 'Reims');
        }
    },
    methods: {
        formatAgencyDisplayName(name) {
            const trimmed = (name || '').trim();

            if (trimmed === '') {
                return 'Ewigo';
            }

            if (trimmed.toLowerCase().startsWith('ewigo')) {
                return trimmed;
            }

            return `Ewigo ${trimmed}`;
        },
        closeModal() {
            this.$emit('close')
        },
        resetForm() {
            this.currentStep = 1
            this.autobizData = false
            this.showInfoTooltip = false
            this.errors = {
                estimation: '',
                email: '',
                phone: '',
                postal_code: ''
            }
            this.form.reset()
            this.form.registration = this.initialRegistration
            this.form.mileage = this.initialMileage
            this.form.reference = this.reference
            this.form.utm = this.utmId
            this.form.finition = ''
            this.form.state = ''
            this.form.prefer_agency = true
            this.form.accept_contact = false
            this.resolvedAgencyContact = null
            this.consentLoading = false
            this.consentResolveRequestId = 0
        },
        buildConsentText(contact) {
            if (!contact) {
                return '';
            }

            const agencyLabel = contact.type === 'france'
                ? `${contact.name} (une des agences Ewigo me rappellera)`
                : contact.name;

            return `J'accepte d'être contacté par ${agencyLabel} par téléphone au sujet de la vente ou de la reprise de mon véhicule, jusqu'à 12 mois. Je peux retirer ce consentement à tout moment, y compris oralement, en appelant le ${contact.phone} ou en écrivant à ${contact.email}, ou directement au sein du point de vente. Je peux obtenir gratuitement, sur simple demande, la preuve de ce consentement.`;
        },
        async fetchResolvedAgency() {
            const postalCode = this.form.postal_code;

            if (!postalCode || !/^\d{5}$/.test(postalCode)) {
                this.resolvedAgencyContact = null;
                this.consentLoading = false;
                return;
            }

            const requestId = ++this.consentResolveRequestId;
            this.consentLoading = true;

            try {
                const response = await axios.get(route('landingpage.resolve.agency'), {
                    params: {
                        postal_code: postalCode,
                        agency_id: this.agency.id,
                    },
                });

                if (requestId !== this.consentResolveRequestId) {
                    return;
                }

                this.resolvedAgencyContact = response.data.data;
            } catch (error) {
                if (requestId !== this.consentResolveRequestId) {
                    return;
                }

                this.resolvedAgencyContact = null;
                console.error('Erreur lors de la résolution de l\'agence:', error);
            } finally {
                if (requestId === this.consentResolveRequestId) {
                    this.consentLoading = false;
                }
            }
        },
        onAgencyPreferenceChange() {
            this.form.accept_contact = false;
        },
        goToStep2() {
            if (!this.form.registration || this.form.registration.length < 9) {
                document.getElementById("errorInput").style.display = "block";
                return
            }
            if (this.form.registration.length == 9 || this.form.registration.length == 11) {
                this.currentStep = 2
                this.getAutobiz()
            }
        },
        getAutobiz() {
            axios.post(route('minisite.autobiz.infos'), {registration: this.form.registration})
                .then((response) => {
                    this.autobizData = response.data
                    if (!response.data.status) {
                        const errorElement = document.getElementById("errorAutobizz");
                        if (errorElement) {
                            errorElement.style.display = "block";
                        }
                        const caracteristiquesElement = document.getElementsByClassName("caracteristiques")[0];
                        if (caracteristiquesElement) {
                            caracteristiquesElement.style.display = "none";
                        }
                    }
                })
        },
        formatNumber(event, field) {
            let value = event.target.value;

            // Nettoyer complètement : garder seulement les chiffres
            const cleanValue = value.replace(/[^0-9]/g, '');

            // Formater avec des espaces pour les milliers
            let formattedValue = '';
            if (cleanValue) {
                formattedValue = cleanValue.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
            }

            // Mettre à jour le champ
            this.form[field] = formattedValue;
            event.target.value = formattedValue;
        },
        validateEstimation() {
            const value = this.form.estimation;
            // Supprimer les espaces pour la validation
            const cleanValue = value ? value.replace(/\s/g, '') : '';
            if (cleanValue && !/^\d+$/.test(cleanValue)) {
                this.errors.estimation = 'L\'estimation doit contenir uniquement des chiffres';
            } else {
                this.errors.estimation = '';
            }
        },
        validateEmail() {
            const value = this.form.email;
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (value && !emailRegex.test(value)) {
                this.errors.email = 'Veuillez saisir une adresse email valide';
            } else {
                this.errors.email = '';
            }
        },
        validatePhone() {
            const value = this.form.phone;
            const phoneRegex = /^[0-9\s\-\+\(\)]+$/;
            if (value && !phoneRegex.test(value)) {
                this.errors.phone = 'Veuillez saisir un numéro de téléphone valide';
            } else {
                this.errors.phone = '';
            }
        },
        validatePostalCode() {
            const value = this.form.postal_code;
            const postalCodeRegex = /^\d{5}$/;
            if (!value) {
                this.errors.postal_code = '';
            } else if (postalCodeRegex.test(value)) {
                this.errors.postal_code = '';
            } else {
                this.errors.postal_code = 'Le code postal doit contenir exactement 5 chiffres';
            }
        },
        toggleInfoTooltip() {
            this.showInfoTooltip = !this.showInfoTooltip;
        },
        validateStep3() {
            // Vérifier les champs obligatoires
            if (!this.form.first_name || !this.form.last_name || !this.form.email || !this.form.city || !this.form.postal_code) {
                alert('Veuillez remplir tous les champs obligatoires');
                return false;
            }

            if (!this.consentReady || this.consentLoading) {
                alert('Veuillez patienter pendant la préparation du consentement');
                return false;
            }

            if (!this.form.accept_contact) {
                alert('Vous devez accepter d\'être contacté pour soumettre le formulaire');
                return false;
            }

            // Vérifier les validations
            if (this.errors.email || this.errors.phone) {
                alert('Veuillez corriger les erreurs dans le formulaire');
                return false;
            }

            return true;
        },
        submitForm() {
            if (this.loadReprise === false) {
                // Valider le formulaire avant envoi
                if (!this.validateStep3()) {
                    return;
                }

                this.loadReprise = true

                // Préparer les données du formulaire
                const formData = {
                    reprise_depot_choice: 'reprise', // Valeur par défaut pour reprise
                    registration: this.form.registration,
                    mileage: this.form.mileage ? this.form.mileage.replace(/\s/g, '') : '',
                    note: this.form.note,
                    state: this.form.state,
                    estimation: this.form.estimation ? this.form.estimation.replace(/\s/g, '') : '',
                    first_name: this.form.first_name,
                    last_name: this.form.last_name,
                    email: this.form.email,
                    city: this.form.city,
                    postal_code: this.form.postal_code,
                    phone: this.form.phone,
                    finition: this.form.finition,
                    reference: this.form.reference,
                    make: this.form.make,
                    model: this.form.model,
                    fuel: this.form.fuel,
                    color: this.form.color,
                    year: this.form.year,
                    utm: this.form.utm,
                    prefer_agency: this.usesSiteAgencyForConsent && this.form.prefer_agency,
                    agency_id: this.consentAgencyId,
                    accept_contact: this.form.accept_contact,
                    consent_text: this.consentText,
                };

                // Si captcha configuré, l'utiliser pour la sécurité
                if (this.captcha) {
                    grecaptcha.enterprise.ready(async () => {
                        try {
                            const token = await grecaptcha.enterprise.execute(this.captcha, {action: 'submit'});
                            formData.token = token;

                            axios.post(route('landingpage.send.email'), formData)
                                .then((response) => {
                                    this.currentStep = 4
                                    trackGenerateLead()
                                    this.$emit('success', response.data)
                                })
                                .catch((error) => {
                                    this.loadReprise = false
                                    console.error('Erreur lors de l\'envoi:', error)
                                    console.error('Réponse d\'erreur:', error.response?.data)
                                })
                        } catch (error) {
                            this.loadReprise = false
                            console.error('Erreur captcha:', error)
                            alert('Erreur de validation. Veuillez réessayer.');
                        }
                    });
                } else {
                    // En local ou si captcha non configuré, envoyer avec token vide
                    console.warn('Captcha non configuré - envoi en mode développement');
                    formData.token = '';

                    axios.post(route('landingpage.send.email'), formData)
                        .then((response) => {
                            this.currentStep = 4
                            trackGenerateLead()
                            this.$emit('success', response.data)
                        })
                        .catch((error) => {
                            this.loadReprise = false
                            console.error('Erreur lors de l\'envoi:', error)
                            console.error('Réponse d\'erreur:', error.response?.data)
                        })
                }
            }
        }
    }
}
</script>

<style lang="scss" scoped>
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.modal-content {
    background: #1e1e1e;
    border-radius: 16px;
    max-width: 700px;
    width: 90%;
    max-height: 90vh;
    overflow-y: auto;
    position: relative;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
}

.modal-header {
    padding: 2rem 2rem 1rem;
    text-align: center;
    position: relative;
    border-bottom: 1px solid #2a3f5f;

    h3 {
        font-family: 'Poppins', sans-serif;
        font-weight: 600;
        font-size: 1.5rem;
        color: white;
        margin: 0 0 0.5rem 0;
    }

    p {
        font-family: 'Poppins', sans-serif;
        font-weight: 400;
        font-size: 1rem;
        color: white;
        margin: 0;
    }

    .close-button {
        position: absolute;
        top: 1rem;
        right: 1rem;
        background: none;
        border: none;
        cursor: pointer;
        padding: 0.5rem;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #ccc;
        transition: all 0.2s ease;

        &:hover {
            background-color: #2a3f5f;
            color: white;
        }
    }
}

.modal-body {
    padding: 1rem 2rem 2rem;

    &:has(.merci) {
        padding: 0;
    }
}

// Styles du formulaire (copiés depuis Landing.vue)
.steps-estimation {
    .block-progression {
        display: flex;
        align-items: center;

        .arrow-number {
            display: flex;
            align-items: center;
            gap: 1rem;
            margin-right: 1rem;

            .previous-arrow {
                display: inline-flex;
                align-items: center;
                justify-content: center;
                width: 28px;
                height: 28px;
                padding: 0;
                border: none;
                background: transparent;
                cursor: pointer;
                border-radius: 6px;
                transition: background-color .2s ease, transform .1s ease;

                &:hover { background: rgba(240, 125, 0, 0.1); }
                &:active { transform: translateY(1px); }

                svg {
                    display: block;
                    transform: rotate(0deg) !important;
                }

                /* safety: reset any transform on the button too */
                transform: none !important;
            }

            span {
                font-family: 'Poppins', sans-serif;
                font-weight: 600;
                color: white;
            }
        }

        .steps-bg {
            flex: 1;
            height: 4px;
            background-color: #e0e0e0;
            border-radius: 2px;
            position: relative;

            .steps-orange {
                height: 100%;
                background-color: #F07D00;
                border-radius: 2px;
                transition: width 0.3s ease;
            }
        }
    }

    .form {
        .label-immat {
            font-family: 'Poppins', sans-serif;
            font-weight: 500;
            color: white;
            margin-bottom: 0.5rem;
            display: block;
        }

        input, select, textarea {
            width: 100%;
            padding: 1rem;
            border: 2px solid #2a3f5f;
            border-radius: 8px;
            font-family: 'Poppins', sans-serif;
            font-size: 1rem;
            margin-bottom: 1rem;
            transition: border-color 0.2s ease;
            background-color: white;
            color: #333;

            &:focus {
                outline: none;
                border-color: #F07D00;
            }
        }

        .select-wrapper {
            position: relative;
            margin-bottom: 1rem;
        }

        .container-etats {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 1rem;
            margin-bottom: 1rem;

            input[type="radio"] {
                display: none;
            }

            .label-radio {
                padding: 1rem;
                border: 2px solid #2a3f5f;
                border-radius: 8px;
                text-align: center;
                cursor: pointer;
                font-family: 'Poppins', sans-serif;
                transition: all 0.2s ease;
                color: white;
                background-color: transparent;

                &:hover {
                    border-color: #F07D00;
                }
            }

            input[type="radio"]:checked + .label-radio {
                background-color: #F07D00;
                color: white;
                border-color: #F07D00;
            }
        }

        .accept {
            display: flex;
            align-items: flex-start;
            gap: 0.5rem;

            input[type="checkbox"] {
                width: auto;
                margin: 0;
            }

            label {
                font-family: 'Poppins', sans-serif;
                font-size: 0.9rem;
                color: #ccc;
                line-height: 1.4;
                padding: 0;
            }

            .consent-loading {
                font-style: italic;
                opacity: 0.85;
            }

            .consent-email-link {
                color: inherit;
                text-decoration: underline;

                &:hover {
                    color: #F07D00;
                }
            }
        }

        hr {
            font-size: 1px;
            border: none;
            border-top: 1px solid #ffffff30;
        }
    }

    .bouton-orange {
        background: #F07D00;
        color: white;
        border: none;
        padding: 1rem 2rem;
        border-radius: 50px;
        font-family: 'Poppins', sans-serif;
        font-weight: 600;
        font-size: 1.1rem;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        transition: all 0.3s ease;
        width: 100%;
        margin-top: 1rem;

        &:hover:not(.disabled) {
            background: #e67300;
            transform: translateY(-2px);
            box-shadow: 0 8px 20px rgba(240, 125, 0, 0.3);
        }

        &.disabled {
            background: #ccc;
            cursor: not-allowed;
            opacity: 0.6;
        }

        svg {
            width: 16px;
            height: 16px;
        }
    }
}

// Styles pour l'étape 2 - Informations véhicule
.vehicle-info {
    margin-bottom: 1.5rem;

    .vehicle-model {
        font-family: 'Poppins', sans-serif;
        font-weight: 600;
        font-size: 1.2rem;
        color: white;
        margin-bottom: 0.8rem;
    }

    .vehicle-details {
        display: flex;
        gap: 2rem;
        margin-bottom: 0.8rem;

        .detail-item {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            color: white;
            font-family: 'Poppins', sans-serif;
            font-size: 0.9rem;

            .detail-icon {
                width: 16px;
                height: 16px;
                filter: brightness(0) invert(1); // Pour rendre les icônes blanches
            }
        }
    }
}

// Styles pour les champs du formulaire étape 2
.form-fields {
    .field-group {
        margin-bottom: 1rem;

        .form-select, .form-input, .form-textarea {
            width: 100%;
            padding: 0.8rem;
            border: 2px solid #2a3f5f;
            border-radius: 8px;
            font-family: 'Poppins', sans-serif;
            font-size: 1rem;
            background-color: white;
            color: #333;
            transition: border-color 0.2s ease;

            &:focus {
                outline: none;
                border-color: #F07D00;
            }

            &::placeholder {
                color: #999;
            }
        }

        .form-select {
            cursor: pointer;
            appearance: none;
            background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23333' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e");
            background-repeat: no-repeat;
            background-position: right 1rem center;
            background-size: 16px;
            padding-right: 3rem;

            &.placeholder-selected {
                color: #999 !important;
            }

            option {
                color: #333;
            }

            option:disabled {
                color: #999;
            }
        }

        .form-textarea {
            resize: vertical;
            min-height: 80px;
        }
    }
}

.simu-plaque {
    background: #2a3f5f;
    color: white;
    padding: 1rem;
    border-radius: 8px;
    text-align: center;
    font-family: 'Poppins', sans-serif;
    font-weight: 600;
    font-size: 1.2rem;
    margin-bottom: 1rem;
}

.hr-grey {
    height: 1px;
    background-color: #2a3f5f;
    margin: 1rem 0;
}

.modele {
    font-family: 'Poppins', sans-serif;
    font-weight: 600;
    color: white;
    margin-bottom: 1rem;

    .caracteristiques {
        display: flex;
        gap: 2rem;
        margin-top: 1rem;

        div {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            font-size: 0.9rem;
            color: #ccc;

            img {
                width: 16px;
                height: 16px;
            }
        }
    }
}

.merci {
    text-align: center;
    padding: 2rem;
    background: #F07D00;
    border-radius: 16px;
    position: relative;
    transform: none !important;

    .close-button {
        position: absolute;
        top: 1rem;
        right: 1rem;
        background: none;
        border: none;
        cursor: pointer;
        padding: 0.5rem;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        transition: all 0.2s ease;

        &:hover {
            background-color: rgba(255, 255, 255, 0.2);
        }
    }

    .title-merci {
        font-family: 'Poppins', sans-serif;
        font-weight: 600;
        font-size: 1rem;
        color: white;
        display: block;
        margin-bottom: 1rem;
    }

    p {
        font-family: 'Poppins', sans-serif;
        color: white;
        margin-bottom: 0;
        line-height: 1.6;
    }
}

.errorImmat {
    display: none;
    color: #ff5151;
    font-size: 0.9rem;
    margin: 0.5rem 0 0;
    font-family: 'Poppins', sans-serif;
}

.error-message {
    color: #ff5151;
    font-size: 0.9rem;
    margin: 0.5rem 0 0;
    font-family: 'Poppins', sans-serif;
}

.aide-etat {
    display: inline-flex;
    align-items: center;
    margin-left: 0.5rem;
    cursor: help;

    img {
        width: 16px;
        height: 16px;
    }
}

// Responsive
@media (max-width: 768px) {
    .modal-content {
        width: 95%;
        margin: 1rem;
    }

    .modal-header {
        padding: 1.5rem 1.5rem 1rem;

        h3 {
            font-size: 1.3rem;
        }
    }

    .modal-body {
        padding: 1rem 1.5rem 1.5rem;
    }

    .container-etats {
        grid-template-columns: 1fr !important;
    }

    .location-fields {
        grid-template-columns: 1fr !important;
    }
}

// Styles pour les champs de localisation
.location-fields {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;

    input {
        margin-bottom: 0;
    }
}

// Styles pour la case à cocher du point de vente
.agency-choice {
    margin-bottom: 1rem;

    .agency-choice-header {
        display: flex;
        align-items: center;
        gap: 0.5rem;

        input[type="checkbox"] {
            width: auto;
            margin: 0;
            cursor: pointer;
            flex-shrink: 0;
        }

        label {
            font-family: 'Poppins', sans-serif;
            font-size: 0.95rem;
            color: white;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            flex: 1;
            margin: 0;

            .agency-name {
                color: #F07D00;
                font-weight: 600;
            }

            .info-button {
                background: none;
                border: none;
                cursor: pointer;
                padding: 0.25rem;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                color: #F07D00;
                transition: all 0.2s ease;
                border-radius: 50%;
                flex-shrink: 0;
                margin-left: 0.25rem;

                &:hover {
                    background-color: rgba(240, 125, 0, 0.1);
                    transform: scale(1.1);
                }

                svg {
                    display: block;
                }
            }
        }
    }

    .info-tooltip {
        animation: slideDown 0.3s ease;

        .tooltip-content {
            border: 1px solid #ffffff30;
            padding: 1rem;
            border-radius: 8px;

            ul {
                list-style: none;
                margin: 0;
                padding: 0;

                li {
                    font-family: 'Poppins', sans-serif;
                    font-size: 0.85rem;
                    color: #ccc;
                    line-height: 1.6;
                    padding-left: 1.5rem;
                    position: relative;

                    &:not(:last-child) {
                        margin-bottom: 0.75rem;
                    }

                    &::before {
                        content: '•';
                        position: absolute;
                        left: 0;
                        font-weight: bold;
                    }

                    strong {
                        color: white;
                        font-weight: 600;
                    }
                }
            }
        }
    }
}

@keyframes slideDown {
    from {
        opacity: 0;
        transform: translateY(-10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

// Styles pour le loader du bouton
.bouton-orange {
    &.loading {
        opacity: 0.8;
        cursor: not-allowed;
        pointer-events: none;
    }

    .spinner {
        animation: rotate 1s linear infinite;

        .spinner-path {
            stroke-dasharray: 90, 150;
            stroke-dashoffset: 0;
            stroke-linecap: round;
            animation: dash 1.5s ease-in-out infinite;
        }
    }
}

@keyframes rotate {
    100% {
        transform: rotate(360deg);
    }
}

@keyframes dash {
    0% {
        stroke-dasharray: 1, 150;
        stroke-dashoffset: 0;
    }
    50% {
        stroke-dasharray: 90, 150;
        stroke-dashoffset: -35;
    }
    100% {
        stroke-dasharray: 90, 150;
        stroke-dashoffset: -124;
    }
}
</style>
